/** The control of an action field — where the data lives (plan D4, §3): the on-site
 *  status offers Check in · Mark no-show · Clear check-in beside its value, each
 *  participant's pre-check-in state a compact menu, the owner its picker.
 *
 *  - Edit mode: every control QUEUES its change in the draft. While the draft is clean it
 *    also offers "… now" (a split button's menu, or the menu's "Save at once" group).
 *  - View mode with a clean draft: "… now" only. With unsaved changes kept in the editor:
 *    it queues into them and opens the editor — never "… now" over a dirty draft.
 *  - The transition the field already holds is not offered.
 *  - Absent — not disabled — when the operator may not run the action, the catalog does
 *    not offer it yet, or its guards fail on this record (the field's own tooltip says
 *    why in edit mode).
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Button, ButtonGroup, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import { useId, useRef, useState } from 'react';

import type { RecordKey } from '../draft/changeSet';
import { useRecordEditor } from '../editor/RecordEditorProvider';
import type { RecordAction, RecordActionTransition, RecordField } from '../editor/types';

import { actionOfField, offeredTransitions, type ActionModes } from './model';
import { useRecordActions, type ActionRequest } from './RecordActionsProvider';

export interface ActionControlProps {
  field: RecordField;
  rec: RecordKey;
  /** Whose value it is, for the accessible names of a party row ("Tom Doe"). */
  subject?: string;
}

export function ActionControl({ field, rec, subject }: ActionControlProps) {
  const { config } = useRecordEditor();
  const actions = useRecordActions();
  const action = actionOfField(field, config.actions);
  if (!action || !actions.isAvailable(action, rec)) return null;
  const modes = actions.modes(action);
  if (!modes.queue && !modes.now) return null;
  if (action.widget) return <WidgetTrigger action={action} rec={rec} />;
  const offered = offeredTransitions(action, actions.valueOf(action, rec));
  if (offered.length === 0) return null;
  return action.scope === 'participant' ? (
    <TransitionMenu action={action} rec={rec} field={field} offered={offered} modes={modes} {...(subject ? { subject } : {})} />
  ) : (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }} data-action-control={action.key}>
      {offered.map((t) => (
        <TransitionButton key={String(t.to)} action={action} rec={rec} t={t} modes={modes} />
      ))}
    </Box>
  );
}

function request(
  action: RecordAction,
  rec: RecordKey,
  t: RecordActionTransition,
  anchorEl: HTMLElement | null,
  subject?: string,
): ActionRequest {
  return { action, rec, to: t.to, label: t.label, anchorEl, confirm: t.confirm ?? null, ...(subject ? { subject } : {}) };
}

const NOW_NOTE = 'Saves just this change — nothing else is in the draft';

/** One transition as a button: queue, "… now", or both as a split button (edit mode with a
 *  clean draft — the main part queues, its menu saves at once). */
function TransitionButton({
  action,
  rec,
  t,
  modes,
}: {
  action: RecordAction;
  rec: RecordKey;
  t: RecordActionTransition;
  modes: ActionModes;
}) {
  const actions = useRecordActions();
  const groupRef = useRef<HTMLDivElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const btnSx = { py: 0.25, whiteSpace: 'nowrap' } as const;

  if (modes.queue && modes.now) {
    return (
      <>
        <ButtonGroup ref={groupRef} size="small" variant="outlined" disabled={actions.busy}>
          <Button sx={btnSx} onClick={(e) => actions.queue(request(action, rec, t, e.currentTarget))} data-transition={String(t.to)}>
            {t.label}
          </Button>
          <Button
            sx={{ px: 0.25, minWidth: 0 }}
            aria-label={`Other ways to ${t.label.toLowerCase()}`}
            aria-haspopup="menu"
            aria-expanded={menuAnchor ? true : undefined}
            aria-controls={menuAnchor ? menuId : undefined}
            onClick={() => setMenuAnchor(groupRef.current)}
          >
            <ArrowDropDownIcon fontSize="small" />
          </Button>
        </ButtonGroup>
        <Menu id={menuId} anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem
            onClick={() => {
              const anchor = groupRef.current;
              setMenuAnchor(null);
              actions.runNow(request(action, rec, t, anchor));
            }}
            sx={{ display: 'block', whiteSpace: 'normal', maxWidth: 300 }}
          >
            <Typography variant="body2">{t.label} now</Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {NOW_NOTE}
            </Typography>
          </MenuItem>
        </Menu>
      </>
    );
  }
  const now = modes.now && !modes.queue;
  return (
    <Button
      size="small"
      variant="outlined"
      sx={btnSx}
      disabled={actions.busy}
      data-transition={String(t.to)}
      onClick={(e) => {
        const req = request(action, rec, t, e.currentTarget);
        if (now) actions.runNow(req);
        else actions.queue(req);
      }}
    >
      {now ? `${t.label} now` : t.label}
    </Button>
  );
}

/** A participant's transitions as one compact menu (a party row has no room for three
 *  buttons): "Add to the draft" and, while the draft is clean, "Save at once". */
function TransitionMenu({
  action,
  rec,
  field,
  offered,
  modes,
  subject,
}: {
  action: RecordAction;
  rec: RecordKey;
  field: RecordField;
  offered: RecordActionTransition[];
  modes: ActionModes;
  subject?: string;
}) {
  const actions = useRecordActions();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const pick = (t: RecordActionTransition, now: boolean) => {
    setOpen(false);
    const req = request(action, rec, t, btnRef.current, subject ? `${field.label} of ${subject}` : undefined);
    if (now) actions.runNow(req);
    else actions.queue(req);
  };
  return (
    <>
      <Button
        ref={btnRef}
        size="small"
        variant="text"
        endIcon={<ArrowDropDownIcon />}
        disabled={actions.busy}
        aria-haspopup="menu"
        aria-expanded={open || undefined}
        aria-controls={open ? menuId : undefined}
        aria-label={`Change ${field.label.toLowerCase()}${subject ? ` of ${subject}` : ''}`}
        onClick={() => setOpen(true)}
        sx={{ py: 0, px: 0.75, minHeight: 28, '& .MuiButton-endIcon': { ml: 0 } }}
        data-action-control={action.key}
      >
        Change
      </Button>
      <Menu id={menuId} anchorEl={btnRef.current} open={open} onClose={() => setOpen(false)}>
        {modes.queue && <ListSubheader sx={{ lineHeight: '30px', fontWeight: 600 }}>Add to the draft</ListSubheader>}
        {modes.queue &&
          offered.map((t) => (
            <MenuItem key={`q:${String(t.to)}`} onClick={() => pick(t, false)}>
              {t.label}
            </MenuItem>
          ))}
        {modes.now && <ListSubheader sx={{ lineHeight: '30px', fontWeight: 600 }}>Save at once</ListSubheader>}
        {modes.now &&
          offered.map((t) => (
            <MenuItem key={`n:${String(t.to)}`} onClick={() => pick(t, true)}>
              {t.label} now
            </MenuItem>
          ))}
      </Menu>
    </>
  );
}

/** A picker-driven action (change owner): opens its picker on the trigger. */
function WidgetTrigger({ action, rec }: { action: RecordAction; rec: RecordKey }) {
  const actions = useRecordActions();
  return (
    <Button
      size="small"
      variant="outlined"
      sx={{ py: 0.25, whiteSpace: 'nowrap' }}
      disabled={actions.busy}
      aria-haspopup="dialog"
      onClick={(e) => actions.openWidget(action, rec, e.currentTarget)}
      data-action-control={action.key}
    >
      {action.label}…
    </Button>
  );
}
